import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Images, Loader2 } from "lucide-react";

import { Button } from "./ui/button";

// The platform screen is hardcoded dark (PlatformAdminPage.jsx:304) while the
// shadcn tokens resolve light, so anything inheriting them came out white on
// white. Palette is pinned to the page's slate scale instead.
const BTN = "border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700 hover:text-white";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shrink photos stored before uploads were downscaled on arrival.
 *
 * Dry run first, always: it reports what WOULD be saved without writing a
 * byte, so the figure can be checked before anything is rewritten.
 */
export const PhotoBackfillCard = ({ authHeaders, tenantSlug = "default" }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(null);
  const [forceSweep, setForceSweep] = useState(false);
  const timer = useRef(null);

  const running = job?.status === "running";

  useEffect(() => {
    if (!job?.id || !running) return undefined;
    timer.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/platform/photos/backfill/${job.id}`, {
          headers: authHeaders(),
        });
        if (res.ok) setJob(await res.json());
      } catch {
        /* keep the last known state rather than blanking the log */
      }
    }, 2000);
    return () => clearInterval(timer.current);
  }, [job?.id, running, authHeaders]);

  const start = async (dryRun, kind = "backfill") => {
    setStarting(`${kind}-${dryRun ? "dry" : "run"}`);
    try {
      const res = await fetch(
        `${API}/platform/photos/${kind === "sweep" ? "sweep" : "backfill"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            tenant_slug: tenantSlug,
            dry_run: dryRun,
            ...(kind === "sweep" ? { force: forceSweep } : {}),
          }),
        },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Could not start");
      setJob({ id: body.job_id, status: "running", log: [], dry_run: dryRun, kind });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStarting(null);
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-800 bg-slate-900 p-4 text-slate-100 shadow-sm"
      data-testid="photo-backfill-card"
    >
      <div className="flex items-center gap-2">
        <Images className="h-4 w-4" />
        <h3 className="text-sm font-bold uppercase tracking-wider">Photo storage</h3>
      </div>

      <p className="mt-2 text-sm text-slate-400">
        Re-encodes photos stored before uploads were shrunk automatically, to
        1600px JPEG — the size already used for insurer emails and incident
        PDFs. Photos keep their place in every job; only the stored bytes
        change. Anything already small, or that won&apos;t decode, is left
        exactly as it is.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          data-testid="photo-backfill-dry-run-btn"
          variant="outline"
          size="sm"
          className={BTN}
          disabled={running || starting !== null}
          onClick={() => start(true)}
        >
          {starting === "backfill-dry" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          1. Dry run
        </Button>
        <Button
          data-testid="photo-backfill-run-btn"
          variant="outline"
          size="sm"
          className={BTN}
          disabled={running || starting !== null}
          onClick={() => start(false)}
        >
          {starting === "backfill-run" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          2. Shrink them
        </Button>
        {running ? (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            {job.dry_run ? "Measuring" : "Working"}…
          </span>
        ) : null}
      </div>

      <p className="mt-2 text-xs text-slate-400">
        Shrinking is one-way — the extra pixels in the originals are not kept.
        Run the dry run first and check the numbers.
      </p>

      <div className="mt-4 border-t border-slate-800 pt-3">
        <h4 className="text-xs font-bold uppercase tracking-wider">
          Clear unreferenced photos
        </h4>
        <p className="mt-1 text-sm text-slate-400">
          Deleting a job, or a photo, doesn&apos;t always remove the stored
          bytes — so they keep costing you. This finds photos nothing points at
          any more and clears them. Yard captures still waiting to be matched
          to a job are never touched, and anything uploaded in the last 24
          hours is left alone in case it&apos;s still being attached.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            data-testid="photo-sweep-dry-run-btn"
            variant="outline"
            size="sm"
            className={BTN}
            disabled={running || starting !== null}
            onClick={() => start(true, "sweep")}
          >
            {starting === "sweep-dry" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            1. Dry run
          </Button>
          <Button
            data-testid="photo-sweep-run-btn"
            variant="outline"
            size="sm"
            className={BTN}
            disabled={running || starting !== null}
            onClick={() => start(false, "sweep")}
          >
            {starting === "sweep-run" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            2. Delete them
          </Button>
        </div>
        {job?.stats?.suspicious ? (
          <label className="mt-2 flex items-start gap-2 text-xs text-amber-400">
            <input
              type="checkbox"
              data-testid="photo-sweep-force"
              className="mt-0.5"
              checked={forceSweep}
              onChange={(e) => setForceSweep(e.target.checked)}
            />
            <span>
              Too many photos looked unreferenced, so nothing was deleted.
              That usually means something is wrong rather than that they are
              all junk. Check a few of the reasons above against real jobs,
              then tick this to go ahead anyway.
            </span>
          </label>
        ) : null}
      </div>

      {job?.log?.length ? (
        <pre
          data-testid="photo-backfill-log"
          className="mt-3 max-h-56 overflow-auto rounded-md border border-slate-800 bg-slate-950 p-3 font-mono text-slate-200 text-[11px] leading-relaxed"
        >
          {job.log.join("\n")}
          {job.error ? `\n${job.error}` : ""}
        </pre>
      ) : null}

      {job?.status === "done" && job?.stats ? (
        <p
          data-testid="photo-backfill-result"
          className="mt-2 text-sm font-medium text-emerald-400"
        >
          {job.kind === "sweep" ? (
            <>
              {job.dry_run ? "Would free" : "Freed"} {job.stats.freed_mb}MB across{" "}
              {job.stats.deleted} unreferenced photo
              {job.stats.deleted === 1 ? "" : "s"} · {job.stats.referenced} still
              in use
            </>
          ) : (
            <>
              {job.dry_run ? "Would save" : "Saved"} {job.stats.saved_mb}MB across{" "}
              {job.stats.shrunk} photo{job.stats.shrunk === 1 ? "" : "s"}
              {job.stats.failed ? ` · ${job.stats.failed} unreadable, left alone` : ""}
            </>
          )}
        </p>
      ) : null}
    </div>
  );
};
